import { useState } from "react";
import { useParams } from "react-router-dom";
import { api, t } from "../../lib/api";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { ErrorNote } from "../../components/ui";
import { roleLabel } from "../../components/Shell";
import type { Engagement } from "../Engagement";

const PHASES = [
  ["mobilise", "Mobilise"],
  ["discover", "Discover"],
  ["diagnose", "Diagnose"],
  ["design", "Design"],
  ["deliver", "Deliver and sustain"],
] as const;

export function SettingsTab({ engagement, onSaved }: { engagement: Engagement; onSaved: () => void }) {
  const { tenant } = useParams();
  const { config } = useSession();
  const team = useAsync(
    () => api.get<Array<{ id: string; email: string; name: string | null; role: string; assigned: boolean }>>(`${t(tenant!)}/engagements/${engagement.id}/team`),
    [tenant, engagement.id],
  );
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const patch = async (body: Record<string, unknown>, label: string) => {
    setError(null);
    setSaved(null);
    try {
      await api.patch(`${t(tenant!)}/engagements/${engagement.id}`, body);
      setSaved(label);
      onSaved();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <div className="stack" style={{ gap: 20, maxWidth: 880 }}>
      <ErrorNote error={error} />
      {saved && <p className="note-box">{saved}</p>}

      <section className="panel">
        <div className="panel-head">
          <h3>Sharing with the client</h3>
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={engagement.publishedToClient}
            onChange={(e) => void patch({ publishedToClient: e.target.checked }, e.target.checked ? "Shared with client viewers." : "No longer shared with client viewers.")}
          />
          <span>
            Client viewers can see the dashboard
            <br />
            <span className="muted small">They see verified, anonymised findings only: never transcripts, recordings or names.</span>
          </span>
        </label>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>Transformation phase</h3>
        </div>
        <div className="row wrap" style={{ gap: 6 }}>
          {PHASES.map(([k, label]) => (
            <button key={k} className={`btn small ${engagement.phase === k ? "primary" : ""}`} onClick={() => void patch({ phase: k }, `Phase set to ${label}.`)}>
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>Focus reviews</h3>
          <p>Turning one on adds its questions to the guide and its checklist to every analysis.</p>
        </div>
        {config.focusModules.map((m) => {
          const on = engagement.focusModules.includes(m.key);
          return (
            <label key={m.key} className="check" style={{ alignItems: "flex-start", marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={on}
                onChange={() =>
                  void patch(
                    { focusModules: on ? engagement.focusModules.filter((k) => k !== m.key) : [...engagement.focusModules, m.key] },
                    on ? "Focus review turned off." : "Focus review turned on; its questions are in the guide.",
                  )
                }
              />
              <span>
                <b>{m.name}</b>
                <br />
                <span className="muted small">{m.summary}</span>
              </span>
            </label>
          );
        })}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>Team on this engagement</h3>
          <p>Leads and admins see every engagement. Interviewers and reviewers only see engagements they are on.</p>
        </div>
        {team.data && (
          <table className="table">
            <tbody>
              {team.data.map((p) => (
                <tr key={p.id}>
                  <td>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={p.assigned}
                        disabled={p.role === "admin" || p.role === "lead"}
                        onChange={async (e) => {
                          const ids = team.data!.filter((x) => (x.id === p.id ? e.target.checked : x.assigned)).map((x) => x.id);
                          await api.put(`${t(tenant!)}/engagements/${engagement.id}/team`, { userIds: ids });
                          await team.reload();
                        }}
                      />
                      <span>{p.name ?? p.email}</span>
                    </label>
                  </td>
                  <td className="muted">{p.email}</td>
                  <td>{roleLabel(p.role)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
